import { postJson } from "./http";
import type { TeamCandidateDTO } from "./teamApi";

/**
 * Escalations & access — the two contact lists (ordered escalation contacts,
 * dashboard access) and the picker directories they draw from. Every endpoint
 * answers with the full screen state; refusals arrive as readable sentences
 * under `errors.message` ("…is already on this list.").
 */

export type ContactListKind = "escalation" | "dashboard";

/**
 * One contact on one of the two lists. A snapshot taken at add time — the row
 * keeps its own name/email/phone; `user_id` links back to master `users` only
 * when the pick came from the firm directory.
 */
export interface ClientContactDTO {
  contact_id: number;
  name: string;
  email: string;
  phone: string | null;
  source: "firm" | "books" | "manual";
  user_id: number | null;
}

/**
 * Who already uses this client's Zoho Books — org users read live (and
 * snapshotted server-side). `connected` false means no Books connection at
 * all (upload/Tally clients — the picker just shows firm-only); `failed` true
 * means the connection exists but the read failed and nothing is on file yet.
 */
export interface BooksDirectoryDTO {
  connected: boolean;
  people: { name: string; email: string; phone: string | null }[];
  failed: boolean;
}

/** The whole screen — every escalations endpoint answers with this. */
export interface EscalationsStateDTO {
  escalation: ClientContactDTO[];
  dashboard: ClientContactDTO[];
  firm: TeamCandidateDTO[];
  books: BooksDirectoryDTO;
}

interface EscalationsEnvelope {
  success: boolean;
  message: string;
  data: EscalationsStateDTO;
}

const escalationsCall = async (path: string, body: Record<string, unknown>): Promise<EscalationsStateDTO> =>
  (await postJson<EscalationsEnvelope>(path, body, ["message"])).data;

export const fetchEscalationsState = (publicId: string): Promise<EscalationsStateDTO> =>
  escalationsCall("/api/onboarding/escalations/state", { public_id: publicId });

/** A firm pick sends `{ user_id }`; a Books or manual add sends name/email(/phone) + source. */
export const addClientContact = (
  publicId: string,
  list: ContactListKind,
  contact: { user_id: number } | { name: string; email: string; phone?: string; source: "books" | "manual" }
): Promise<EscalationsStateDTO> =>
  escalationsCall("/api/onboarding/escalations/add", { public_id: publicId, list, ...contact });

export const removeClientContact = (publicId: string, contactId: number): Promise<EscalationsStateDTO> =>
  escalationsCall("/api/onboarding/escalations/remove", { public_id: publicId, contact_id: contactId });

export const moveClientContact = (
  publicId: string,
  contactId: number,
  direction: "up" | "down"
): Promise<EscalationsStateDTO> =>
  escalationsCall("/api/onboarding/escalations/move", { public_id: publicId, contact_id: contactId, direction });
